if (!customElements.get('bambu-header-menu')) {
  class BambuHeaderMenu extends HTMLElement {
    constructor() {
      super();

      this.openTimer = null;
      this.closeTimer = null;
      this.details = [];
      this.overlay = null;
      this.detailObserver = null;
      this.seriesMenus = [];

      this.handleEscape = this.handleEscape.bind(this);
      this.handleOutsidePointer = this.handleOutsidePointer.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
    }

    connectedCallback() {
      this.details = Array.from(
        this.querySelectorAll('.bambu-custom-navigation__item > details-disclosure > details')
      );
      this.overlay = document.querySelector('.header-menu__overlay-element');

      this.details.forEach((detail) => {
        detail.addEventListener('mouseenter', () => this.queueOpen(detail));
        detail.addEventListener('mouseleave', () => this.queueClose(detail));
        detail.addEventListener('focusin', () => this.open(detail));
        detail.addEventListener('focusout', () => {
          window.setTimeout(() => {
            if (!detail.contains(document.activeElement)) this.close(detail);
          }, 0);
        });
      });

      this.seriesMenus = Array.from(this.querySelectorAll('[data-bambu-menu-panels]'));
      this.seriesMenus.forEach((menu) => this.setupSeriesMenu(menu));

      this.detailObserver = new MutationObserver(() => this.syncOverlay());
      this.details.forEach((detail) => {
        this.detailObserver.observe(detail, {
          attributes: true,
          attributeFilter: ['open'],
        });
      });

      document.addEventListener('keydown', this.handleEscape);
      document.addEventListener('pointerdown', this.handleOutsidePointer);
      window.addEventListener('resize', this.handleViewportChange);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.detailObserver?.disconnect();
      document.removeEventListener('keydown', this.handleEscape);
      document.removeEventListener('pointerdown', this.handleOutsidePointer);
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('scroll', this.handleViewportChange);
      this.hideOverlay();
    }

    queueOpen(detail) {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.openTimer);
      this.openTimer = window.setTimeout(() => this.open(detail), 80);
    }

    setupSeriesMenu(menu) {
      const detail = menu.closest('details');
      const links = Array.from(detail?.querySelectorAll('[data-bambu-menu-panel-target]') || []);
      const panels = Array.from(menu.querySelectorAll('[data-bambu-menu-panel]'));

      if (!links.length || !panels.length) return;

      const activate = (target) => {
        const hasPanel = panels.some((panel) => panel.dataset.bambuMenuPanel === target);
        if (!hasPanel) return;

        links.forEach((link) => {
          link.classList.toggle('is-active', link.dataset.bambuMenuPanelTarget === target);
        });
        panels.forEach((panel) => {
          panel.hidden = panel.dataset.bambuMenuPanel !== target;
        });
      };

      links.forEach((link) => {
        const target = link.dataset.bambuMenuPanelTarget;
        link.addEventListener('mouseenter', () => activate(target));
        link.addEventListener('focus', () => activate(target));
      });

      activate(menu.dataset.defaultPanel || links[0].dataset.bambuMenuPanelTarget);
    }

    queueClose(detail) {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.close(detail), 140);
    }

    open(detail) {
      window.clearTimeout(this.closeTimer);

      this.details.forEach((item) => {
        if (item !== detail) item.removeAttribute('open');
      });

      detail.removeAttribute('closing');
      detail.setAttribute('open', '');

      this.syncOverlay();
    }

    close(detail) {
      detail.removeAttribute('open');
      detail.removeAttribute('opening');
      detail.removeAttribute('closing');

      this.syncOverlay();
    }

    closeAll() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.details.forEach((detail) => {
        detail.removeAttribute('open');
        detail.removeAttribute('opening');
        detail.removeAttribute('closing');
      });
      this.hideOverlay();
    }

    hideOverlay() {
      if (
        this.overlay?.dataset.overlayOwner &&
        this.overlay.dataset.overlayOwner !== 'desktop-menu'
      ) return;

      this.overlay.classList.remove('shown', 'bambu-header-overlay-active');
      this.overlay.style.removeProperty('top');
      delete this.overlay.dataset.overlayOwner;
    }

    syncOverlay() {
      if (this.dataset.showOverlay !== 'true' || !this.details.some((item) => item.open)) {
        this.hideOverlay();
        return;
      }

      this.positionOverlay();
      if (!this.overlay) return;

      this.overlay.dataset.overlayOwner = 'desktop-menu';
      this.overlay.classList.remove('menu-drawer-overlay-active');
      this.overlay.classList.add('bambu-header-overlay-active', 'shown');
    }

    positionOverlay() {
      if (!this.overlay) return;

      const header = this.closest('header');
      if (!header) return;

      const headerBottom = Math.max(0, Math.min(window.innerHeight, header.getBoundingClientRect().bottom));
      this.overlay.style.top = `${Math.round(headerBottom)}px`;
    }

    handleViewportChange() {
      if (this.details.some((item) => item.open)) this.positionOverlay();
    }

    handleOutsidePointer(event) {
      if (!this.contains(event.target)) this.closeAll();
    }

    handleEscape(event) {
      if (event.key !== 'Escape') return;

      const openedDetail = this.details.find((detail) => detail.open);
      if (!openedDetail) return;

      const summary = openedDetail.querySelector(':scope > summary');
      this.closeAll();
      summary?.focus();
    }
  }

  customElements.define('bambu-header-menu', BambuHeaderMenu);
}
